import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { useConsent } from "@/contexts/ConsentContext";
import { ChevronDown, ChevronUp, Shield } from "lucide-react";

export default function CookieBanner() {
  const { consent, bannerVisible, acceptAll, acceptEssential, updateCategories } = useConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);
  const [marketing, setMarketing] = useState(consent?.marketing ?? false);

  // Restore saved selections when banner is reopened
  useEffect(() => {
    if (bannerVisible && consent) {
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
    }
  }, [bannerVisible, consent]);

  if (!bannerVisible) return null;

  const handleSaveSelection = () => {
    updateCategories(analytics, marketing);
  };

  return (
    <>
      {/* Backdrop — erzwingt Aufmerksamkeit, ohne zu sperren */}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm animate-in fade-in duration-300"
      />
      <div
        role="dialog"
        aria-modal="false"
        aria-labelledby="cookie-banner-title"
        className="fixed bottom-0 inset-x-0 z-50 p-3 sm:p-6 animate-in slide-in-from-bottom-8 duration-500"
      >
        <div className="mx-auto max-w-5xl rounded-2xl border border-border border-t-4 border-t-primary bg-card shadow-2xl ring-1 ring-primary/10">
          <div className="p-5 sm:p-7">
            <div className="flex items-start gap-4 mb-5">
              <div className="rounded-xl bg-accent/10 p-2.5 shrink-0">
                <Shield className="h-6 w-6 text-accent" />
              </div>
              <div className="flex-1">
                <h3 id="cookie-banner-title" className="font-display font-bold text-foreground text-lg sm:text-xl mb-1.5">
                  Wir respektieren Ihre Privatsphäre
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Wir verwenden Cookies und ähnliche Technologien. Technisch notwendige Cookies sind für den Betrieb erforderlich. Analyse- und Marketing-Cookies werden nur mit Ihrer Zustimmung gesetzt.{" "}
                <Link to="/datenschutz" className="text-primary hover:underline">
                  Datenschutzerklärung
                </Link>
              </p>
            </div>
          </div>

          {showDetails && (
            <div className="mb-4 space-y-3 rounded-xl border border-border bg-muted/30 p-4">
              <div className="flex items-center gap-3">
                <Checkbox id="ck-essential" checked disabled className="opacity-70" />
                <label htmlFor="ck-essential" className="text-sm text-foreground font-medium">
                  Notwendig <span className="text-muted-foreground font-normal">(immer aktiv)</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground ml-7">Sitzungstoken, Cookie-Einstellungen, Sidebar-Status.</p>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="ck-analytics"
                  checked={analytics}
                  onCheckedChange={(v) => setAnalytics(v === true)}
                />
                <label htmlFor="ck-analytics" className="text-sm text-foreground font-medium cursor-pointer">
                  Statistik / Analyse
                </label>
              </div>
              <p className="text-xs text-muted-foreground ml-7">Google Analytics 4 — Seitenaufrufe, Verweildauer, Geräteinfo. IP-Anonymisierung aktiv.</p>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="ck-marketing"
                  checked={marketing}
                  onCheckedChange={(v) => setMarketing(v === true)}
                />
                <label htmlFor="ck-marketing" className="text-sm text-foreground font-medium cursor-pointer">
                  Marketing
                </label>
              </div>
              <p className="text-xs text-muted-foreground ml-7">Google Ads Conversion-Tracking — Messung der Wirksamkeit von Anzeigen.</p>
            </div>
          )}

            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              <Button
                onClick={acceptAll}
                size="lg"
                className="flex-1 sm:flex-none sm:min-w-[200px] bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-lg shadow-primary/30 text-base"
              >
                Alle akzeptieren
              </Button>
              {showDetails && (
                <Button onClick={handleSaveSelection} size="lg" variant="secondary" className="flex-1 sm:flex-none">
                  Auswahl speichern
                </Button>
              )}
              <Button
                onClick={acceptEssential}
                size="lg"
                variant="outline"
                className="flex-1 sm:flex-none"
              >
                Nur notwendige
              </Button>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="inline-flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors sm:ml-auto px-3 py-2"
              >
                {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {showDetails ? "Weniger" : "Einstellungen"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
